import type { RealtimeClient } from '@openai/realtime-api-beta';

type SetFn<T = any> = (value: T | ((prev: T) => T)) => void;

interface RegisterGetPropertyDeps {
  setFilter: SetFn<any>;
  setToTop: (v: boolean) => void;
  setTopInvestments: SetFn<any>;
  setTopProperties: SetFn<any>;
  filterProperties: (data: any) => Promise<any>;
}

export function registerGetPropertyTool(
  client: RealtimeClient,
  {
    setFilter,
    setToTop,
    setTopInvestments,
    setTopProperties,
    filterProperties,
  }: RegisterGetPropertyDeps
) {
  client.addTool(
    {
      name: 'get_property',
      description:
        'Search for properties based on user query. Ensure all parameters are in English.',
      parameters: {
        type: 'object',
        properties: {
          purpose: {
            type: 'string',
            description: 'Purpose of listing.',
            enum: ['rent', 'sale'],
          },
          type: {
            type: 'string',
            description: 'Main property category.',
            enum: ['residential', 'commercial', 'plot'],
          },
          subtype: {
            type: 'string',
            description: 'Specific property subtype.',
            enum: [
              'house',
              'flat',
              'upperPortion',
              'lowerPortion',
              'room',
              'farmHouse',
              'guestHouse',
              'hostel',
              'annexe',
              'pentHouse',
              'hotelSuites',
              'basement',
              'commercialPlot',
              'agriculturalLand',
              'industrialLand',
              'plotFile',
              'farmhousePlot',
              'shop',
              'warehouse',
              'building',
              'hall',
              'theatre',
              'gym',
              'plaza',
              'land',
              'foodCourt',
              'factory',
            ],
          },
          area: {
            type: 'string',
            description:
              'Area or sector name (e.g., "Soan Garden", "F-10"). Use commas to search multiple areas.',
          },
          city: {
            type: 'string',
            description:
              "City name is compulsory. If the user doesn't mention a city name, ask for it first before calling the tool (e.g., 'Islamabad', 'Lahore', 'Karachi').",
          },
          priceMin: { type: 'number', description: 'Minimum property price in PKR.' },
          priceMax: { type: 'number', description: 'Maximum property price in PKR.' },
          sizeMin: {
            type: 'number',
            description: 'Minimum property size (e.g., 5, 10, 1000).',
          },
          sizeMax: {
            type: 'number',
            description: 'Maximum property size (e.g., 100, 5000, 80000).',
          },
          sizeUnit: {
            type: 'string',
            description: 'Size unit.',
            enum: ['sqm', 'sqyd', 'marla', 'kanal', 'sqft'],
          },
          bed: {
            type: ['integer', 'string'],
            description:
              "Number of bedrooms the user asked for (e.g., 1, 2, 3, 4, 5, 6, 7, 8, 9, 10+). If the user asks for multiple bedroom options, such as 5 and above, pass the values as comma-separated, like '5, 6, 7, 8, 9, 10+'.",
          },
          bath: {
            type: ['integer', 'string'],
            description:
              "Number of bathrooms the user asked for (e.g., 1, 2, 3, 4, 5, 6, 7, 8, 9, 10+). If the user asks for multiple bathroom options, such as 5 and above, pass the values as comma-separated, like '5, 6, 7, 8, 9, 10+'.",
          },
        },
        required: ['purpose', 'city'],
      },
    },
    async (key: { [key: string]: any }) => {
      const data = { ...key, page: 1, pageSize: 50 };
      setFilter(data);

      const filterRes: any = await filterProperties(data);
      if (filterRes?.error) return filterRes?.message;

      const { properties, count } = filterRes;

      setToTop(true);
      setTopInvestments({ plans: [], count: 0, currentCount: 0 });
      setTopProperties({
        properties,
        count,
        currentCount: properties?.length,
      });

      return properties?.length
        ? `Properties available: ${properties?.length}`
        : 'No property available!';
    }
  );
}
